import { useCallback, useEffect, useMemo, useState } from "react";

import { PalAutoComplete, PalUtils } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SortableContainer, SortableElement } from "react-sortable-hoc";

import { Box, Grid } from "@mui/material";

import PRDragHandle from "~components/Generic/PRDragHandle";
import PRModal from "~components/Generic/PRModal";
import { getAllStatisticPages, getPagesSettings, updatePages } from "~store/statisticPages/actions";
import { selectAllStatisticPages, selectOrderedPages } from "~store/statisticPages/selectors";
import { selectCurrentProjectId } from "~store/user/selectors";

const VisibilitySettingsModalContent = ({ get }) => {
  const { t } = useTranslation();
  const orderedPagesState = useSelector(selectOrderedPages);
  const statisticPages = useSelector(selectAllStatisticPages);
  const projectId = useSelector(selectCurrentProjectId);
  const dispatch = useDispatch();

  const [orderedPages, setOrderedPages] = useState(orderedPagesState);

  const orderedPagesIds = useMemo(() => {
    return orderedPages.map((page) => page.id);
  }, [orderedPages]);

  const selectOptions = useMemo(() => {
    const options = statisticPages.map((page) => ({
      label: page.name,
      value: page.id,
    }));
    // .filter((page) => !orderedPages.some((orderedPage) => orderedPage.id === page.value));
    return options;
  }, [statisticPages]);

  useEffect(() => {
    setOrderedPages(orderedPagesState);
  }, [orderedPagesState]);

  useEffect(() => {
    dispatch(getPagesSettings(projectId));
    dispatch(getAllStatisticPages(projectId));
  }, [dispatch, projectId]);

  const handleSelectedPagesChange = useCallback(
    (e, newPagesIds) => {
      // don't break the order of pages

      const newPages = statisticPages.filter((page) => newPagesIds.some((newPageId) => newPageId === page.id));
      //
      const newOrderedPages = orderedPages.filter((page) => newPages.some((newPage) => newPage.id === page.id));

      const addedPages = newPages.filter((newPage) => !orderedPages.some((page) => page.id === newPage.id));
      setOrderedPages([...newOrderedPages, ...addedPages]);
    },
    [orderedPages, statisticPages]
  );

  const onClose = () => {
    get(false)();
  };

  const onSubmit = async () => {
    await dispatch(updatePages(projectId, orderedPagesIds));
    await dispatch(getPagesSettings(projectId));
    get(true)();
  };

  const onIndexChange = ({ oldIndex, newIndex }) => {
    const updatedPages = PalUtils.arrayMove(orderedPages, oldIndex, newIndex);
    setOrderedPages([...updatedPages]);
  };

  return (
    <PRModal
      size="lg"
      submitText={t("common.save")}
      title={t("dashboard.statisticPages.visibilitySettings")}
      onClick={onSubmit}
      onClose={onClose}
    >
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12}>
          <PalAutoComplete
            fullWidth
            isPrimitiveValue
            multiple
            label={t("dashboard.statisticPages.visibilitySettingsSelectLabel")}
            options={selectOptions}
            value={orderedPagesIds}
            onChange={handleSelectedPagesChange}
          />
        </Grid>
        <Grid item xs={12}>
          <PageList useDragHandle items={orderedPages} onSortEnd={onIndexChange} />
        </Grid>
      </Grid>
    </PRModal>
  );
};

const PageList = SortableContainer(({ items }) => {
  return (
    <Grid container spacing={2}>
      {items.map((item, index) => (
        <PageItem key={`item-${index}`} index={index} item={item} />
      ))}
    </Grid>
  );
});
const PageItem = SortableElement(({ item }) => {
  return (
    <Box sx={{ width: "100%", padding: 1, background: "#fff" }} zIndex={4999}>
      <Grid container alignItems="center">
        <Grid item xs={1}>
          <PRDragHandle absolute={false} />
        </Grid>
        <Grid item xs={11}>
          {item.name}
        </Grid>
      </Grid>
    </Box>
  );
});

const VisibilitySettingsModal = withCardon(VisibilitySettingsModalContent, { destroyOnHide: true });

export default VisibilitySettingsModal;
